<template>
  <div class="kanban-container">
    <!-- Menu superior com seletor de funil e botões -->
    <div v-if="hasFunnels" class="kanban-header flex flex-col gap-3 p-3 bg-surface-raised border-b border-line-faint">
      <template v-if="isCompactFiltersLayout">
        <div class="flex items-end justify-between gap-3">
          <div class="relative flex min-w-0 flex-1 flex-col gap-1" @click.stop>
            <span class="px-1 text-sm font-medium text-subtle">
              {{ $t('KANBAN.FILTERS.FUNNEL') }}
            </span>
            <button
              class="kanban-filter-trigger inline-flex w-full items-center justify-between px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
              :title="selectedFunnel ? selectedFunnel.name : $t('KANBAN.FUNNEL.SELECT')"
              @click="toggleFunnelSelector"
            >
              <span class="truncate">{{ funnelButtonLabel }}</span>
              <fluent-icon icon="chevron-down" class="ml-2 flex-shrink-0" size="12"/>
            </button>

            <div
              v-if="showFunnelSelector"
              class="kanban-dropdown-pane absolute left-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none"
            >
              <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                <button
                  v-for="funnel in funnels"
                  :key="funnel.id"
                  class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                  :title="funnel.name"
                  @click="selectFunnel(funnel)"
                >
                  {{ truncateText(funnel.name) }}
                </button>
                <div v-if="funnels.length === 0" class="px-4 py-2 text-sm text-slate-300">
                  {{ $t('KANBAN.FUNNEL.NO_FUNNELS') }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="canCreateFunnels || (selectedFunnel && (canEditFunnels || canDeleteFunnels))"
            class="relative flex shrink-0 items-end"
            @click.stop
          >
            <button
              class="inline-flex items-center justify-center w-[38px] h-[38px] border border-line-strong rounded-md text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
              title="Funnel actions"
              aria-label="Funnel actions"
              @click="toggleFunnelActionsSelector"
            >
              <fluent-icon icon="more-vertical" size="16"/>
            </button>

            <div
              v-if="showFunnelActionsSelector"
              class="kanban-dropdown-pane absolute right-0 top-full z-10 mt-1 w-44 rounded-lg focus:outline-none"
            >
              <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                <button
                  v-if="canCreateFunnels"
                  class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] items-center rounded-md px-2 py-2 text-sm"
                  @click="openCreateFunnelFromMenu"
                >
                  <fluent-icon icon="add" class="mr-2" size="12"/>
                  {{ $t('KANBAN.FUNNEL.NEW') }}
                </button>
                <button
                  v-if="canEditFunnels"
                  class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] items-center rounded-md px-2 py-2 text-sm"
                  @click="openEditFunnelFromMenu"
                >
                  <fluent-icon icon="edit" class="mr-2" size="12"/>
                  {{ $t('KANBAN.FUNNEL.EDIT') }}
                </button>
                <button
                  v-if="canDeleteFunnels"
                  class="kanban-dropdown-item kanban-dropdown-item--danger mx-2 flex w-[calc(100%-1rem)] items-center rounded-md px-2 py-2 text-sm"
                  @click="openDeleteFunnelFromMenu"
                >
                  <fluent-icon icon="delete" class="mr-2" size="12"/>
                  {{ $t('KANBAN.FUNNEL.DELETE') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <transition name="mobile-filters-slide">
          <div v-if="showMobileSecondaryFilters" class="grid grid-cols-3 gap-3">
            <div class="relative flex min-w-0 flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.ASSIGNEE') }}
              </span>
              <button
                class="kanban-filter-trigger inline-flex w-full items-center justify-between px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleAssigneeSelector"
              >
                <span class="truncate">{{ getAssigneeFilterLabel }}</span>
                <fluent-icon icon="chevron-down" class="ml-2 flex-shrink-0" size="12"/>
              </button>

              <div
                v-if="showAssigneeSelector"
                class="kanban-dropdown-pane absolute left-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none max-h-80 overflow-y-auto"
              >
                <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                  <button
                    v-for="agent in assigneeFilterOptions"
                    :key="agent.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="selectAssigneeFilter(agent.value)"
                  >
                    {{ agent.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="relative flex min-w-0 flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.TEAM') }}
              </span>
              <button
                class="kanban-filter-trigger inline-flex w-full items-center justify-between px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleTeamSelector"
              >
                <span class="truncate">{{ getTeamFilterLabel }}</span>
                <fluent-icon icon="chevron-down" class="ml-2 flex-shrink-0" size="12"/>
              </button>

              <div
                v-if="showTeamSelector"
                class="kanban-dropdown-pane absolute left-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none max-h-80 overflow-y-auto"
              >
                <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                  <button
                    v-for="team in teamFilterOptions"
                    :key="team.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="selectTeamFilter(team.value)"
                  >
                    {{ team.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="relative flex min-w-0 flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.CARD') }}
              </span>
              <button
                class="kanban-filter-trigger inline-flex w-full items-center justify-between px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleCardTypeSelector"
              >
                <span class="truncate">{{ getCardTypeLabel }}</span>
                <fluent-icon icon="chevron-down" class="ml-2 flex-shrink-0" size="12"/>
              </button>

              <div
                v-if="showCardTypeSelector"
                class="kanban-dropdown-pane absolute right-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none"
              >
                <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                  <button
                    v-for="type in cardTypeOptions"
                    :key="type.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] items-center gap-2 whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="toggleCardType(type.value)"
                  >
                    <span
                      class="inline-flex h-4 w-4 items-center justify-center rounded-md border text-[10px] leading-none"
                      :class="isCardTypeSelected(type.value) ? 'border-woot-500 bg-woot-500 text-white' : 'border-line-strong'"
                    >
                      {{ isCardTypeSelected(type.value) ? '✓' : '' }}
                    </span>
                    {{ type.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="relative flex min-w-0 flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.SORT_ORDER.LABEL') }}
              </span>
              <button
                class="kanban-filter-trigger inline-flex w-full items-center justify-between px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleSortOrderSelector"
              >
                <span class="truncate">{{ getSortOrderLabel }}</span>
                <fluent-icon
                  icon="chevron-down"
                  class="ml-2 flex-shrink-0"
                  size="12"
                />
              </button>

              <div
                v-if="showSortOrderSelector"
                class="kanban-dropdown-pane absolute left-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none"
              >
                <div
                  class="py-2 space-y-1"
                  role="menu"
                  aria-orientation="vertical"
                >
                  <button
                    v-for="option in sortOrderOptions"
                    :key="option.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="selectSortOrder(option.value)"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <div class="flex justify-end leading-none">
          <button
            class="inline-flex items-center gap-0.5 p-0 m-0 border-0 outline-none ring-0 shadow-none bg-transparent text-[10px] leading-none font-medium text-subtle hover:text-subtle focus:text-subtle"
            @click="toggleMobileSecondaryFilters"
          >
            <span>
              {{
                showMobileSecondaryFilters
                  ? $t('KANBAN.FILTERS.SHOW_LESS')
                  : $t('KANBAN.FILTERS.SHOW_MORE')
              }}
            </span>
            <fluent-icon
              icon="chevron-down"
              size="12"
              class="transition-transform duration-200"
              :class="showMobileSecondaryFilters ? 'rotate-180' : 'rotate-0'"
            />
          </button>
        </div>
      </template>

      <template v-else>
        <div class="flex items-center gap-2">
          <div class="relative flex flex-col gap-1" @click.stop>
            <span class="px-1 text-sm font-medium text-subtle">
              {{ $t('KANBAN.FILTERS.FUNNEL') }}
            </span>
            <button 
              class="kanban-filter-trigger inline-flex items-center px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
              :title="selectedFunnel ? selectedFunnel.name : $t('KANBAN.FUNNEL.SELECT')"
              @click="toggleFunnelSelector"
            >
              <span>{{ funnelButtonLabel }}</span>
              <fluent-icon icon="chevron-down" class="ml-2" size="12"/>
            </button>

            <div 
              v-if="showFunnelSelector" 
              class="kanban-dropdown-pane absolute left-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none"
            >
              <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                <button
                  v-for="funnel in funnels"
                  :key="funnel.id"
                  class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                  :title="funnel.name"
                  @click="selectFunnel(funnel)"
                >
                  {{ truncateText(funnel.name) }}
                </button>
                <div v-if="funnels.length === 0" class="px-4 py-2 text-sm text-slate-300">
                  {{ $t('KANBAN.FUNNEL.NO_FUNNELS') }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-end gap-2">
            <div class="relative inline-flex flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.ASSIGNEE') }}
              </span>
              <button
                class="kanban-filter-trigger inline-flex items-center px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleAssigneeSelector"
              >
                <span>{{ getAssigneeFilterLabel }}</span>
                <fluent-icon icon="chevron-down" class="ml-2" size="12"/>
              </button>

              <div
                v-if="showAssigneeSelector"
                class="kanban-dropdown-pane absolute right-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none max-h-80 overflow-y-auto"
              >
                <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                  <button
                    v-for="agent in assigneeFilterOptions"
                    :key="agent.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="selectAssigneeFilter(agent.value)"
                  >
                    {{ agent.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="relative inline-flex flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.TEAM') }}
              </span>
              <button
                class="kanban-filter-trigger inline-flex items-center px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleTeamSelector"
              >
                <span>{{ getTeamFilterLabel }}</span>
                <fluent-icon icon="chevron-down" class="ml-2" size="12"/>
              </button>

              <div
                v-if="showTeamSelector"
                class="kanban-dropdown-pane absolute right-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none max-h-80 overflow-y-auto"
              >
                <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                  <button
                    v-for="team in teamFilterOptions"
                    :key="team.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="selectTeamFilter(team.value)"
                  >
                    {{ team.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="relative inline-flex flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.CARD') }}
              </span>
              <button 
                class="kanban-filter-trigger inline-flex items-center px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleCardTypeSelector"
              >
                <span>{{ getCardTypeLabel }}</span>
                <fluent-icon icon="chevron-down" class="ml-2" size="12"/>
              </button>

              <div 
                v-if="showCardTypeSelector" 
                class="kanban-dropdown-pane absolute right-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none"
              >
                <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                  <button
                    v-for="type in cardTypeOptions"
                    :key="type.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] items-center gap-2 whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="toggleCardType(type.value)"
                  >
                    <span
                      class="inline-flex h-4 w-4 items-center justify-center rounded-md border text-[10px] leading-none"
                      :class="isCardTypeSelected(type.value) ? 'border-woot-500 bg-woot-500 text-white' : 'border-line-strong'"
                    >
                      {{ isCardTypeSelected(type.value) ? '✓' : '' }}
                    </span>
                    {{ type.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="relative inline-flex flex-col gap-1" @click.stop>
              <span class="px-1 text-sm font-medium text-subtle">
                {{ $t('KANBAN.FILTERS.SORT_ORDER.LABEL') }}
              </span>
              <button
                class="kanban-filter-trigger inline-flex items-center px-4 py-2 border border-line-strong rounded-md text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                @click="toggleSortOrderSelector"
              >
                <span>{{ getSortOrderLabel }}</span>
                <fluent-icon icon="chevron-down" class="ml-2" size="12" />
              </button>

              <div
                v-if="showSortOrderSelector"
                class="kanban-dropdown-pane absolute right-0 top-full z-10 mt-1 min-w-full max-w-[calc(100vw-2rem)] rounded-lg focus:outline-none"
              >
                <div
                  class="py-2 space-y-1"
                  role="menu"
                  aria-orientation="vertical"
                >
                  <button
                    v-for="option in sortOrderOptions"
                    :key="option.value"
                    class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] whitespace-nowrap rounded-md text-left px-2 py-2 text-sm"
                    @click="selectSortOrder(option.value)"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="canCreateFunnels || (selectedFunnel && (canEditFunnels || canDeleteFunnels))"
              class="relative inline-block"
              @click.stop
            >
              <button
                class="inline-flex items-center justify-center w-[38px] h-[38px] border border-line-strong rounded-md text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
                title="Funnel actions"
                aria-label="Funnel actions"
                @click="toggleFunnelActionsSelector"
              >
                <fluent-icon icon="more-vertical" size="16"/>
              </button>

              <div
                v-if="showFunnelActionsSelector"
                class="kanban-dropdown-pane absolute right-0 top-full z-10 mt-1 w-44 rounded-lg focus:outline-none"
              >
                <div class="py-2 space-y-1" role="menu" aria-orientation="vertical">
                  <button
                    v-if="canCreateFunnels"
                  class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] items-center rounded-md px-2 py-2 text-sm"
                    @click="openCreateFunnelFromMenu"
                  >
                    <fluent-icon icon="add" class="mr-2" size="12"/>
                    {{ $t('KANBAN.FUNNEL.NEW') }}
                  </button>
                  <button
                    v-if="canEditFunnels"
                  class="kanban-dropdown-item mx-2 flex w-[calc(100%-1rem)] items-center rounded-md px-2 py-2 text-sm"
                    @click="openEditFunnelFromMenu"
                  >
                    <fluent-icon icon="edit" class="mr-2" size="12"/>
                    {{ $t('KANBAN.FUNNEL.EDIT') }}
                  </button>
                  <button
                    v-if="canDeleteFunnels"
                  class="kanban-dropdown-item kanban-dropdown-item--danger mx-2 flex w-[calc(100%-1rem)] items-center rounded-md px-2 py-2 text-sm"
                    @click="openDeleteFunnelFromMenu"
                  >
                    <fluent-icon icon="delete" class="mr-2" size="12"/>
                    {{ $t('KANBAN.FUNNEL.DELETE') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    
    <!-- Área principal do Kanban -->
    <div
      ref="kanbanBoardContainer"
      class="kanban-board-container p-3 bg-surface-sunken h-full overflow-auto"
      @dragover="handleBoardDragOver"
      @drop="stopDragBoardScroll"
    >
      <!-- Estado de carregamento -->
      <div v-if="isLoading('funnels')" class="flex justify-center items-center h-96">
        <spinner size="medium" />
        <span class="ml-2 text-muted">{{ $t('KANBAN.LOADING') }}</span>
      </div>
      
      <!-- Estado de erro -->
      <div v-else-if="getError('funnels')" class="flex flex-col items-center justify-center h-96">
        <div class="text-center p-8 bg-surface-raised rounded-lg border border-line max-w-lg">
          <fluent-icon icon="error-circle" size="64" class="mx-auto mb-4 text-red-500"/>
          <h3 class="text-lg font-medium text-body mb-2">{{ $t('KANBAN.ERROR.TITLE') }}</h3>
          <p class="text-subtle mb-4">{{ getError('funnels') }}</p>
          <button 
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 focus:outline-none"
            @click="loadInitialData"
          >
            <fluent-icon icon="arrow-rotate-clockwise" class="mr-2" size="12"/>
            {{ $t('KANBAN.ACTIONS.RETRY') }}
          </button>
        </div>
      </div>
      
      <!-- Estado vazio - sem funis -->
      <div v-else-if="funnels.length === 0" class="flex flex-col items-center justify-center h-96">
        <div class="text-center p-8 bg-surface-raised rounded-lg border border-line max-w-lg">
          <fluent-icon icon="kanban" size="64" class="mx-auto mb-4 text-slate-400"/>
          <h3 class="text-lg font-medium text-body mb-2">{{ $t('KANBAN.EMPTY.TITLE') }}</h3>
          <p class="text-subtle mb-4">{{ $t('KANBAN.EMPTY.DESCRIPTION') }}</p>
          <button 
            v-if="canCreateFunnels"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 focus:outline-none"
            @click="showCreateFunnelModal = true"
          >
            <fluent-icon icon="add" class="mr-2" size="12"/>
            {{ $t('KANBAN.FUNNEL.CREATE') }}
          </button>
        </div>
      </div>
      
      <!-- Funil selecionado - exibição de colunas -->
      <div v-else-if="selectedFunnel" class="kanban-board flex gap-3 pr-3 h-full">
        <div 
          v-for="label in selectedFunnelLabels" 
          :key="label.id"
          :data-drop-label-id="label.id"
          class="kanban-column bg-surface-raised rounded-md flex flex-col min-w-[320px] max-w-[320px] h-full overflow-hidden"
        >
          <!-- Cabeçalho da coluna -->
          <div 
            class="column-header p-3 border-b border-line-faint font-medium"
            :style="{ 'border-top': `3px solid ${label.color || '#1f93ff'}` }"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center overflow-hidden flex-grow mr-2">
                <span 
                  class="color-dot w-3 h-3 rounded-full mr-2 flex-shrink-0" 
                  :style="{ 'background-color': label.color || '#1f93ff' }"
                ></span>
                <h3 class="text-body truncate" :title="label.title">{{ label.title }}</h3>
                <span class="text-subtle ml-2 text-sm flex-shrink-0">({{ getMetaForLabel(label.id).total_count || 0 }})</span>
              </div>
              <button
                class="inline-flex items-center gap-1.5 p-2 rounded-md border border-woot-200 bg-woot-50 text-woot-700 dark:bg-woot-800/40 dark:border-woot-700 dark:text-woot-100 hover:bg-woot-100 hover:border-woot-300 dark:hover:bg-woot-700/50 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800 text-xs font-semibold flex-shrink-0 transition-colors"
                @click="openColumnDetails(label)"
                :title="$t('KANBAN.COLUMN_DETAILS.ACTION')"
                :aria-label="$t('KANBAN.COLUMN_DETAILS.ACTION')"
              >
                <fluent-icon icon="info" size="20"/>
                <span>{{ $t('KANBAN.COLUMN_DETAILS.ACTION') }}</span>
              </button>
            </div>
            
            <!-- Search Bar -->
            <div class="mt-2 relative">
              <input 
                type="text" 
                :value="columnSearchTerms[label.id]" 
                @input="$set(columnSearchTerms, label.id, $event.target.value)"
                @keyup.enter="searchColumn(label.id, columnSearchTerms[label.id])"
                :placeholder="$t('KANBAN.SEARCH_PLACEHOLDER') || 'Pesquisar...'"
                title="Pesquise por nome, email, telefone, ID ou campos personalizados"
                class="w-full !pl-2 !pr-8 py-0.5 text-[11px] border border-line-strong rounded-lg bg-surface-raised text-body focus:outline-none focus:ring-1 focus:ring-woot-500 !mb-0"
              />
              <button 
                class="absolute right-2 top-1/2 -translate-y-1/2 text-subtle hover:text-muted"
                @click="searchColumn(label.id, columnSearchTerms[label.id])"
              >
                <fluent-icon icon="search" size="20"/>
              </button>
            </div>
          </div>
          
          <!-- Cards na coluna -->
          <div 
            class="column-content p-2 pb-12 flex-grow overflow-y-auto"
            :data-drop-label-id="label.id"
            @scroll="handleColumnScroll($event, label.id)"
            @dragover.prevent
            @drop="dropCard($event, label.id)"
          >
            <!-- Estado de carregamento inicial -->
            <div v-if="isLoading('cards') && !getCardsForLabel(label.id).length" class="flex justify-center items-center py-4">
              <spinner size="small" />
            </div>
            
            <!-- Cards da coluna -->
            <kanban-card
              v-else
              v-for="card in getFilteredCardsForLabel(label.id)" 
              :key="card.id"
              :card="card"
              :label-id="label.id"
              :column-color="label.color || '#1f93ff'"
              :funnel-contact-attribute-ids="selectedFunnelContactAttributeIds"
              :funnel-conversation-attribute-ids="selectedFunnelConversationAttributeIds"
              @dragstart="dragStart"
              @dragend="handleDragEnd"
              @touch-drag-start="startTouchCardDrag"
              @touch-drag-move="handleTouchCardDragMove"
              @touch-drag-end="endTouchCardDrag"
              @touch-drag-cancel="cancelTouchCardDrag"
              @card-click="openCardSidebar"
              class="mb-2"
            />
            
            <!-- Estado vazio da coluna -->
            <div 
              v-if="!isLoading('cards') && getFilteredCardsForLabel(label.id).length === 0" 
              class="empty-column-state p-4 text-center border border-dashed border-line-strong rounded-md my-2"
              @dragover.prevent
              @drop="dropCard($event, label.id)"
            >
              <p class="text-sm text-subtle !mb-0">{{ $t('KANBAN.TAGS.DRAG_CARDS') }}</p>
            </div>
            
            <!-- Loading de paginação -->
            <div v-if="loadingMore[label.id]" class="flex flex-col justify-center items-center py-4 bg-surface-hover rounded-lg mt-4 mb-8 border border-line-faint">
               <spinner size="small" />
               <span class="text-[10px] mt-1 text-subtle uppercase font-medium tracking-wider">
                 {{ $t('KANBAN.LOADING') }}
               </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="touchDragPreview"
      class="touch-drag-preview"
      :style="touchDragPreviewStyle"
    >
      <div
        class="touch-drag-preview-card"
        :style="{ width: `${touchDragPreview.width}px` }"
        v-html="touchDragPreview.html"
      />
    </div>
    
    <!-- Modal de criar funil -->
    <div v-if="showCreateFunnelModal" class="modal-overlay fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50" @click.self="showCreateFunnelModal = false">
      <div class="modal-content bg-surface-raised rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 class="text-lg font-medium text-body mb-4">{{ $t('KANBAN.FUNNEL.CREATE_TITLE') }}</h2>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.FUNNEL.NAME') }}</label>
          <input 
            type="text" 
            v-model="newFunnel.name" 
            class="w-full !px-3 py-2 border border-line-strong rounded-lg focus:outline-none focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:text-white !mb-1 !mb-0"
            :placeholder="$t('KANBAN.FUNNEL.NAME_PLACEHOLDER')"
          />
          <span v-if="!newFunnel.name" class="text-xs text-red-500 mt-1">
            {{ $t('KANBAN.FUNNEL.REQUIRED_FIELD') }}
          </span>
        </div>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.TAGS.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedLabelsForNewFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :taggable="true"
              :show-labels="false"
              :tag-placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL')"
              :options="formattedLabels"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
              class="!mb-1"
              @tag="createLabelForNewFunnel"
            >
              <template slot="option" slot-scope="{ option }">
                <div class="flex items-center">
                  <span
                    v-if="!option.isTag"
                    class="inline-block w-3 h-3 rounded-full mr-2"
                    :style="{ 'background-color': option.color }"
                  ></span>
                  <span>{{ option.isTag ? option.label : option.name }}</span>
                </div>
              </template>
              <template slot="tag" slot-scope="{ option, remove }">
                <span class="multiselect__tag">
                  <span 
                    class="inline-block w-2 h-2 rounded-full mr-1" 
                    :style="{ 'background-color': option.color }"
                  ></span>
                  <span>{{ option.name }}</span>
                  <i 
                    aria-hidden="true" 
                    tabindex="1" 
                    class="multiselect__tag-icon" 
                    @click="remove(option)"
                  ></i>
                </span>
              </template>
            </multiselect>
          </div>
          <span v-if="newFunnel.selectedLabelIds.length === 0" class="text-xs text-red-500 mt-1">
            {{ $t('KANBAN.FUNNEL.REQUIRED_FIELD') }}
          </span>
        </div>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.CONTACT_ATTRIBUTES.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedContactAttributesForNewFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedContactAttributes"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
              <template slot="tag" slot-scope="{ option, remove }">
                <span class="multiselect__tag">
                  <span>{{ option.name }}</span>
                  <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon" @click="remove(option)"></i>
                </span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.CONVERSATION_ATTRIBUTES.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedConversationAttributesForNewFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedConversationAttributes"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
            </multiselect>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.TEAMS.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedTeamsForNewFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedTeams"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
            </multiselect>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.AGENTS.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedAgentsForNewFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedAgents"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
            </multiselect>
          </div>
        </div>

        <div class="mb-4" v-if="newFunnel.selectedLabelIds.length > 0">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.TAGS.ORDER_INFO') }}</label>
          <div class="border border-line-strong rounded-lg p-2 dark:bg-slate-700">
            <div 
              v-for="(labelId, index) in newFunnel.selectedLabelIds" 
              :key="labelId"
              class="flex items-center p-2 hover:bg-surface-hover rounded-md mb-1 cursor-move"
              draggable="true"
              @dragstart="startDragLabel($event, index, 'create')"
              @dragover.prevent
              @drop="dropLabel($event, index, 'create')"
              @dragenter.prevent
            >
              <!-- Numeração da ordem -->
              <span class="flex items-center justify-center w-6 h-6 text-xs font-medium text-subtle bg-surface-hover rounded-full mr-2">
                {{ index + 1 }}
              </span>
              <span 
                class="inline-block w-3 h-3 rounded-full mr-2" 
                :style="{ 'background-color': getLabelById(labelId).color || '#1f93ff' }"
              ></span>
              <span class="text-sm text-body flex-grow">{{ getLabelById(labelId).title }}</span>
              <div class="ml-auto flex items-center">
                <button 
                  class="text-subtle hover:text-subtle"
                  @click="moveTagUp(index)" 
                  :disabled="index === 0"
                >
                  <fluent-icon icon="chevron-up" size="14"/>
                </button>
                <button 
                  class="text-subtle hover:text-subtle ml-1"
                  @click="moveTagDown(index)" 
                  :disabled="index === newFunnel.selectedLabelIds.length - 1"
                >
                  <fluent-icon icon="chevron-down" size="14"/>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end gap-2 mt-6">
          <button 
            class="px-4 py-2 border border-line-strong rounded-lg text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
            @click="showCreateFunnelModal = false"
          >
            {{ $t('KANBAN.ACTIONS.CANCEL') }}
          </button>
          <button 
            class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isLoading('create') || !newFunnel.name || newFunnel.selectedLabelIds.length === 0"
            @click="createFunnel"
          >
            <spinner v-if="isLoading('create')" size="small" class="mr-1" />
            {{ $t('KANBAN.ACTIONS.CREATE') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Modal de editar funil -->
    <div v-if="showEditFunnelModal" class="modal-overlay fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50" @click.self="showEditFunnelModal = false">
      <div class="modal-content bg-surface-raised rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 class="text-lg font-medium text-body mb-4">{{ $t('KANBAN.FUNNEL.EDIT_TITLE') }}</h2>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.FUNNEL.NAME') }}</label>
          <input 
            type="text" 
            v-model="editingFunnel.name" 
            class="w-full !px-3 py-2 border border-line-strong rounded-lg focus:outline-none focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:text-white !mb-1 !mb-0"
          />
          <span v-if="!editingFunnel.name" class="text-xs text-red-500 mt-1">
            {{ $t('KANBAN.FUNNEL.REQUIRED_FIELD') }}
          </span>
        </div>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.TAGS.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedLabelsForEditingFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :taggable="true"
              :show-labels="false"
              :tag-placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL')"
              :options="formattedLabels"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
              class="!mb-1"
              @tag="createLabelForEditingFunnel"
            >
              <template slot="option" slot-scope="{ option }">
                <div class="flex items-center">
                  <span
                    v-if="!option.isTag"
                    class="inline-block w-3 h-3 rounded-full mr-2"
                    :style="{ 'background-color': option.color }"
                  ></span>
                  <span>{{ option.isTag ? option.label : option.name }}</span>
                </div>
              </template>
              <template slot="tag" slot-scope="{ option, remove }">
                <span class="multiselect__tag">
                  <span 
                    class="inline-block w-2 h-2 rounded-full mr-1" 
                    :style="{ 'background-color': option.color }"
                  ></span>
                  <span>{{ option.name }}</span>
                  <i 
                    aria-hidden="true" 
                    tabindex="1" 
                    class="multiselect__tag-icon" 
                    @click="remove(option)"
                  ></i>
                </span>
              </template>
            </multiselect>
          </div>
          <span v-if="editingFunnel.selectedLabelIds.length === 0" class="text-xs text-red-500 mt-1">
            {{ $t('KANBAN.FUNNEL.REQUIRED_FIELD') }}
          </span>
        </div>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.CONTACT_ATTRIBUTES.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedContactAttributesForEditingFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedContactAttributes"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
              <template slot="tag" slot-scope="{ option, remove }">
                <span class="multiselect__tag">
                  <span>{{ option.name }}</span>
                  <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon" @click="remove(option)"></i>
                </span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.CONVERSATION_ATTRIBUTES.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedConversationAttributesForEditingFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedConversationAttributes"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
            </multiselect>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.TEAMS.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedTeamsForEditingFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedTeams"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
            </multiselect>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.AGENTS.SELECT') }}</label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="selectedAgentsForEditingFunnelModel"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              :options="formattedAgents"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :max-height="200"
            >
            </multiselect>
          </div>
        </div>

        <div class="mb-4" v-if="editingFunnel.selectedLabelIds.length > 0">
          <label class="block text-sm font-medium text-muted mb-1">{{ $t('KANBAN.TAGS.ORDER_INFO') }}</label>
          <div class="border border-line-strong rounded-lg p-2 dark:bg-slate-700">
            <div 
              v-for="(labelId, index) in editingFunnel.selectedLabelIds" 
              :key="labelId"
              class="flex items-center p-2 hover:bg-surface-hover rounded-md mb-1 cursor-move"
              draggable="true"
              @dragstart="startDragLabel($event, index, 'edit')"
              @dragover.prevent
              @drop="dropLabel($event, index, 'edit')"
              @dragenter.prevent
            >
              <!-- Numeração da ordem -->
              <span class="flex items-center justify-center w-6 h-6 text-xs font-medium text-subtle bg-surface-hover rounded-full mr-2">
                {{ index + 1 }}
              </span>
              <span 
                class="inline-block w-3 h-3 rounded-full mr-2" 
                :style="{ 'background-color': getLabelById(labelId).color || '#1f93ff' }"
              ></span>
              <span class="text-sm text-body flex-grow">{{ getLabelById(labelId).title }}</span>
              <div class="ml-auto flex items-center">
                <button 
                  class="text-subtle hover:text-subtle"
                  @click="moveEditTagUp(index)" 
                  :disabled="index === 0"
                >
                  <fluent-icon icon="chevron-up" size="14"/>
                </button>
                <button 
                  class="text-subtle hover:text-subtle ml-1"
                  @click="moveEditTagDown(index)" 
                  :disabled="index === editingFunnel.selectedLabelIds.length - 1"
                >
                  <fluent-icon icon="chevron-down" size="14"/>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end gap-2 mt-6">
          <button 
            class="px-4 py-2 border border-line-strong rounded-lg text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
            @click="showEditFunnelModal = false"
          >
            {{ $t('KANBAN.ACTIONS.CANCEL') }}
          </button>
          <button 
            class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isLoading('update') || !editingFunnel.name || editingFunnel.selectedLabelIds.length === 0"
            @click="updateFunnel"
          >
            <spinner v-if="isLoading('update')" size="small" class="mr-1" />
            {{ $t('KANBAN.ACTIONS.SAVE') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Modal de excluir funil -->
    <div v-if="showDeleteFunnelModal" class="modal-overlay fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50" @click.self="showDeleteFunnelModal = false">
      <div class="modal-content bg-surface-raised rounded-lg p-6 w-full max-w-md">
        <h2 class="text-lg font-medium text-body mb-2">{{ $t('KANBAN.FUNNEL.DELETE_TITLE') }}</h2>
        <p class="text-subtle mb-4">
          {{ $t('KANBAN.FUNNEL.CONFIRMATION', { name: selectedFunnel ? selectedFunnel.name : '' }) }}
        </p>
        
        <div class="flex justify-end gap-2 mt-6">
          <button 
            class="px-4 py-2 border border-line-strong rounded-lg text-sm font-medium text-muted bg-surface-raised hover:bg-surface-hover focus:outline-none"
            @click="showDeleteFunnelModal = false"
          >
            {{ $t('KANBAN.ACTIONS.CANCEL') }}
          </button>
          <button 
            class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
            :disabled="isLoading('delete')"
            @click="deleteFunnel"
          >
            <spinner v-if="isLoading('delete')" size="small" class="mr-1" />
            {{ $t('KANBAN.ACTIONS.DELETE') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Column Details Drawer -->
    <div 
      v-if="showColumnDetailsDrawer" 
      class="fixed inset-0 sm:inset-y-0 sm:right-0 sm:left-auto w-full sm:w-96 bg-surface-raised z-40 transform transition-transform duration-300 ease-in-out sm:border-l border-line flex flex-col"
    >
      <div class="p-4 border-b border-line-faint flex justify-between items-center bg-surface-sunken">
        <h2 class="text-lg font-medium text-body flex items-center">
          <span 
            v-if="selectedColumnDetails"
            class="color-dot w-3 h-3 rounded-full mr-2" 
            :style="{ 'background-color': selectedColumnDetails.label.color || '#1f93ff' }"
          ></span>
          Detalhes da Coluna
        </h2>
        <button 
          class="text-subtle hover:text-muted"
          @click="closeColumnDetails"
        >
          <fluent-icon icon="dismiss" size="16"/>
        </button>
      </div>

      <div class="flex-grow overflow-y-auto p-4" v-if="isFetchingColumnDetails">
        <div class="flex justify-center items-center h-full">
          <spinner size="medium" />
        </div>
      </div>
      
      <div class="flex-grow overflow-y-auto p-4" v-else-if="selectedColumnDetails">
        <!-- Selector inside drawer -->
        <div class="mb-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-muted mb-2">Filtrar por tipo</label>
            <div class="grid grid-cols-2 gap-2">
              <button
                v-for="type in cardTypeOptions"
                :key="type.value"
                type="button"
                class="inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm"
                :class="isCardTypeSelected(type.value) ? 'border-woot-500 bg-woot-50 text-woot-700 dark:bg-woot-900/30 dark:text-woot-200' : 'border-line-strong bg-surface-raised text-muted'"
                @click="toggleCardType(type.value); fetchColumnDetails(currentDetailLabelId)"
              >
                  {{ type.label }}
              </button>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-muted mb-2">Buscar</label>
            <div class="relative">
              <input 
                type="text" 
                v-model="drawerSearchTerm" 
                @keyup.enter="fetchColumnDetails(currentDetailLabelId)"
                :placeholder="$t('KANBAN.SEARCH_PLACEHOLDER') || 'Pesquisar...'"
                title="Pesquise por nome, email, telefone, ID ou campos personalizados"
                class="w-full !pl-3 !pr-8 py-2 text-sm border border-line-strong rounded-lg bg-surface-raised text-body focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
              <button 
                class="absolute right-3 top-1/2 -translate-y-1/2 text-subtle hover:text-muted"
                @click="fetchColumnDetails(currentDetailLabelId)"
              >
                <fluent-icon icon="search" size="14"/>
              </button>
            </div>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Info básica -->
          <div class="bg-surface-sunken p-4 rounded-lg border border-line-faint">
            <h3 class="font-medium text-body mb-1 text-lg">{{ selectedColumnDetails.label.title }}</h3>
            <p v-if="selectedColumnDetails.label.description" class="text-sm text-subtle mt-2">{{ selectedColumnDetails.label.description }}</p>
            
            <div class="mt-4 grid grid-cols-2 gap-4">
              <div>
                <p class="text-xs text-subtle uppercase tracking-wider font-semibold">Total de Cartões</p>
                <p class="text-xl font-bold text-body mt-1">{{ selectedColumnDetails.total_count }}</p>
              </div>
              <div>
                <p class="text-xs text-subtle uppercase tracking-wider font-semibold">Tarefas Associadas</p>
                <p class="text-xl font-bold text-body mt-1">{{ selectedColumnDetails.total_tasks_count }}</p>
              </div>
            </div>
            
            <div class="mt-4" v-if="selectedColumnDetails.label.stagnation_days > 0">
              <p class="text-xs text-subtle uppercase tracking-wider font-semibold">Regra de Estagnação</p>
              <p class="text-sm text-body mt-1">{{ selectedColumnDetails.label.stagnation_days }} dias</p>
            </div>
          </div>

          <!-- Distribuição por Responsável -->
          <div v-if="selectedColumnDetails.assignees_distribution && selectedColumnDetails.assignees_distribution.length > 0">
            <h4 class="font-medium text-body mb-3 flex items-center">
              <fluent-icon icon="person" size="16" class="mr-2 text-woot-500"/>
              Responsáveis / Atendentes
            </h4>
            
            <div class="space-y-2">
              <div 
                v-for="(assignee, index) in selectedColumnDetails.assignees_distribution" 
                :key="index"
                class="flex justify-between items-center bg-surface-raised p-2 rounded-lg border border-line"
              >
                <span class="text-sm text-muted">{{ assignee.name }}</span>
                <span class="font-medium bg-surface-hover px-2 py-0.5 rounded-md text-xs text-body">{{ assignee.count }}</span>
              </div>
            </div>
          </div>

          <!-- Atributos Agregados -->
          <div v-if="selectedColumnDetails.aggregated_attributes && selectedColumnDetails.aggregated_attributes.length > 0">
            <h4 class="font-medium text-body mb-3 flex items-center">
              <fluent-icon icon="data-bar-vertical" size="16" class="mr-2 text-woot-500"/>
              Informações Agregadas
            </h4>
            
            <div class="space-y-4">
              <div 
                v-for="(attr, index) in selectedColumnDetails.aggregated_attributes" 
                :key="index"
                class="bg-surface-raised p-3 rounded-lg border border-line"
              >
                <p class="text-sm font-medium text-muted mb-2">{{ attr.name }}</p>
                
                <div v-if="['number', 'currency', 'percent'].includes(attr.type)" class="grid grid-cols-2 gap-2 bg-surface-sunken p-2 rounded-md">
                  <div>
                    <p class="text-xs text-subtle">Total</p>
                    <p class="text-sm font-semibold text-body">
                      <span v-if="attr.type === 'currency'">R$ </span>
                      {{ attr.sum ? attr.sum.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '0' }}
                      <span v-if="attr.type === 'percent'">%</span>
                    </p>
                  </div>
                  <div>
                    <p class="text-xs text-subtle">Média</p>
                    <p class="text-sm font-semibold text-body">
                      <span v-if="attr.type === 'currency'">R$ </span>
                      {{ attr.avg ? attr.avg.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '0' }}
                      <span v-if="attr.type === 'percent'">%</span>
                    </p>
                  </div>
                </div>
                
                <div v-else-if="attr.type === 'list'" class="space-y-1">
                  <div 
                    v-for="(count, item) in attr.counts" 
                    :key="item"
                    class="flex justify-between items-center text-sm bg-surface-sunken px-2 py-1 rounded-md"
                  >
                    <span class="text-muted">{{ item }}</span>
                    <span class="font-medium bg-surface-hover px-2 py-0.5 rounded-md text-xs">{{ count }}</span>
                  </div>
                </div>
                
                <div v-else>
                  <p class="text-sm text-body flex items-center">
                    <span class="font-medium text-lg mr-2">{{ attr.count }}</span> preenchimentos
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="activeSidebarType === 'contact'">
      <div
        v-if="isCardSidebarLoading || !currentSidebarContact"
        class="fixed inset-y-0 right-0 z-50 flex w-full sm:w-[24rem] items-center justify-center bg-surface"
      >
        <spinner size="medium" />
      </div>
      <div
        v-else
        class="fixed inset-y-0 right-0 z-50 w-full sm:w-[24rem] overflow-y-auto bg-surface"
      >
        <ContactInfoPanel
          :contact="currentSidebarContact"
          :on-close="closeCardSidebar"
          close-icon-name="chevron-right"
          :is-standalone-page="true"
        />
      </div>
    </template>

    <template v-if="activeSidebarType === 'conversation'">
      <div class="fixed inset-y-0 right-0 z-50 w-full sm:w-[24rem] bg-surface">
        <div
          v-if="isCardSidebarLoading"
          class="flex h-full items-center justify-center bg-surface"
        >
          <spinner size="medium" />
        </div>
        <ContactPanel
          v-else-if="activeSidebarConversationId"
          class="h-full"
          :conversation-id="activeSidebarConversationId"
          :inbox-id="activeSidebarInboxId"
          :on-toggle="closeCardSidebar"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Spinner from 'shared/components/Spinner';
import KanbanCard from './components/KanbanCard';
import ContactPanel from '../conversation/ContactPanel.vue';
import ContactInfoPanel from '../contacts/components/ContactInfoPanel.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { useAlert } from 'dashboard/composables';
import {
  hasPermissions,
  getUserPermissions,
} from 'dashboard/helper/permissionsHelper';

const UNASSIGNED_FILTER_VALUE = '__unassigned__';
const DEFAULT_CARD_TYPES = ['contact', 'conversation', 'company'];
// A ordenação de hoje (antes desta troca ficar disponível): mais recente
// primeiro, por data de atividade/atualização. `desc` é o valor default para
// manter esse comportamento em quem já usa o kanban sem tocar no seletor.
const DEFAULT_SORT_ORDER = 'desc';
const SORT_ORDERS = ['asc', 'desc'];

const getStoredSortOrder = () => {
  const stored = localStorage.getItem('kanban_sort_order');
  return SORT_ORDERS.includes(stored) ? stored : DEFAULT_SORT_ORDER;
};

const getStoredCardTypes = () => {
  try {
    const storedTypes = JSON.parse(localStorage.getItem('kanban_card_types') || '[]');
    if (Array.isArray(storedTypes) && storedTypes.length) {
      return storedTypes.filter(type => DEFAULT_CARD_TYPES.includes(type));
    }
  } catch (error) {
    // Ignora valores antigos inválidos do localStorage.
  }

  const legacyType = localStorage.getItem('kanban_card_type');
  if (DEFAULT_CARD_TYPES.includes(legacyType)) {
    return [legacyType];
  }

  return [...DEFAULT_CARD_TYPES];
};

export default {
  name: 'KanbanBoard',
  components: {
    Spinner,
    KanbanCard,
    ContactPanel,
    ContactInfoPanel,
    Multiselect,
  },
  
  data() {
    return {
      showColumnDetailsDrawer: false,
      selectedColumnDetails: null,
      isFetchingColumnDetails: false,
      currentDetailLabelId: null,
      drawerSearchTerm: '',
      activeSidebarType: null,
      activeSidebarConversationId: null,
      activeSidebarInboxId: null,
      activeSidebarContactId: null,
      isCardSidebarLoading: false,

      showFunnelSelector: false,
      showCreateFunnelModal: false,
      showEditFunnelModal: false,
      showDeleteFunnelModal: false,
      showFunnelActionsSelector: false,
      draggingCard: null,
      draggingFromLabelId: null,
      dragScrollDirection: null,
      dragScrollTimeoutId: null,
      touchDragPreview: null,
      dragGhostImage: null,
      
      // Novo filtro de tipo de cards
      showCardTypeSelector: false,
      showAssigneeSelector: false,
      showTeamSelector: false,
      showMobileSecondaryFilters: false,
      selectedCardTypes: getStoredCardTypes(),
      selectedAssigneeId: localStorage.getItem('kanban_assignee_id') || '',
      selectedTeamId: localStorage.getItem('kanban_team_id') || '',
      selectedSortOrder: getStoredSortOrder(),
      showSortOrderSelector: false,
      viewportWidth:
        typeof window !== 'undefined' ? window.innerWidth : 1024,
      
      // Termos de pesquisa por coluna
      columnSearchTerms: {},
      
      newFunnel: {
        name: '',
        selectedLabelIds: [],
        selectedContactAttributeIds: [],
        selectedConversationAttributeIds: [],
        selectedTeamIds: [],
        selectedAssigneeIds: [],
      },
      
      editingFunnel: {
        id: null,
        name: '',
        selectedLabelIds: [],
        selectedContactAttributeIds: [],
        selectedConversationAttributeIds: [],
        selectedTeamIds: [],
        selectedAssigneeIds: [],
      },
      
      selectedLabelsForNewFunnelModel: [],
      selectedLabelsForEditingFunnelModel: [],
      selectedContactAttributesForNewFunnelModel: [],
      selectedConversationAttributesForNewFunnelModel: [],
      selectedTeamsForNewFunnelModel: [],
      selectedAgentsForNewFunnelModel: [],
      selectedTeamsForEditingFunnelModel: [],
      selectedAgentsForEditingFunnelModel: [],
      
      loadingMore: {},
    };
  },
  
  computed: {
    ...mapGetters({
      funnels: 'kanban/getFunnels',
      availableLabels: 'kanban/getAvailableLabels',
      selectedFunnelId: 'kanban/getSelectedFunnelId',
      selectedFunnel: 'kanban/getSelectedFunnel',
      cardsByLabel: 'kanban/getCardsByLabel',
      getCardsForLabel: 'kanban/getCardsForLabel',
      getMetaForLabel: 'kanban/getMetaForLabel', // Novo getter
      isLoading: 'kanban/isLoading',
      getError: 'kanban/getError',
      // Corrigido: Usar o getter correto que retorna uma função de filtro
      attributesByModel: 'attributes/getAttributesByModel',
      teams: 'teams/getTeams',
      agents: 'agents/getAgents',
      getConversationById: 'getConversationById',
      getContactRecord: 'contacts/getContact',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
      currentUserRole: 'getCurrentRole',
    }),
    
    // Opções para o seletor de tipo de cards
    cardTypeOptions() {
      return [
        { value: 'all', label: this.$t('KANBAN.KANBAN_FILTER.ALL') },
        { value: 'contact', label: this.$t('KANBAN.KANBAN_FILTER.CONTACTS') },
        { value: 'conversation', label: this.$t('KANBAN.KANBAN_FILTER.CONVERSATIONS') },
        { value: 'company', label: this.$t('KANBAN.KANBAN_FILTER.COMPANIES') }
      ];
    },
    
    // Label para o botão de filtro
    getCardTypeLabel() {
      if (this.selectedCardTypes.length === DEFAULT_CARD_TYPES.length) {
        return this.$t('KANBAN.KANBAN_FILTER.ALL');
      }

      return this.cardTypeOptions
        .filter(option => this.selectedCardTypes.includes(option.value))
        .map(option => option.label)
        .join(', ');
    },
    // Opções para o seletor de ordenação por data
    sortOrderOptions() {
      return [
        {
          value: 'desc',
          label: this.$t('KANBAN.FILTERS.SORT_ORDER.NEWEST_FIRST'),
        },
        {
          value: 'asc',
          label: this.$t('KANBAN.FILTERS.SORT_ORDER.OLDEST_FIRST'),
        },
      ];
    },
    getSortOrderLabel() {
      const selectedOption = this.sortOrderOptions.find(
        option => option.value === this.selectedSortOrder
      );
      return selectedOption
        ? selectedOption.label
        : this.$t('KANBAN.FILTERS.SORT_ORDER.NEWEST_FIRST');
    },
    assigneeFilterOptions() {
      return [
        { value: '', label: this.$t('KANBAN.FILTERS.ALL_ASSIGNEES') },
        {
          value: UNASSIGNED_FILTER_VALUE,
          label: this.$t('KANBAN.FILTERS.UNASSIGNED_ASSIGNEE'),
        },
        ...this.formattedAgents.map(agent => ({
          value: String(agent.id),
          label: agent.name,
        })),
      ];
    },

    teamFilterOptions() {
      return [
        { value: '', label: this.$t('KANBAN.FILTERS.ALL_TEAMS') },
        {
          value: UNASSIGNED_FILTER_VALUE,
          label: this.$t('KANBAN.FILTERS.UNASSIGNED_TEAM'),
        },
        ...this.formattedTeams.map(team => ({
          value: String(team.id),
          label: team.name,
        })),
      ];
    },

    getAssigneeFilterLabel() {
      const selectedOption = this.assigneeFilterOptions.find(
        option => option.value === this.selectedAssigneeId
      );
      return selectedOption
        ? selectedOption.label
        : this.$t('KANBAN.FILTERS.ALL_ASSIGNEES');
    },
    getTeamFilterLabel() {
      const selectedOption = this.teamFilterOptions.find(
        option => option.value === this.selectedTeamId
      );
      return selectedOption
        ? selectedOption.label
        : this.$t('KANBAN.FILTERS.ALL_TEAMS');
    },
    funnelButtonLabel() {
      return this.selectedFunnel
        ? this.truncateText(this.selectedFunnel.name)
        : this.$t('KANBAN.FUNNEL.SELECT');
    },
    isCompactFiltersLayout() {
      return this.viewportWidth <= 425;
    },

    hasFunnels() {
      return Array.isArray(this.funnels) && this.funnels.length > 0;
    },

    touchDragPreviewStyle() {
      if (!this.touchDragPreview) {
        return {};
      }

      return {
        transform: `translate(${this.touchDragPreview.x - this.touchDragPreview.offsetX}px, ${this.touchDragPreview.y - this.touchDragPreview.offsetY}px)`,
      };
    },
    
    // Obter labels do funil selecionado
    selectedFunnelLabels() {
      if (!this.selectedFunnel) return [];
      
      // Preservar a ordem dos label_ids mapeando-os para seus objetos correspondentes
      return this.selectedFunnel.label_ids.map(labelId => 
        this.availableLabels.find(label => label.id === labelId)
      ).filter(Boolean);
    },

    // Formatar labels para o multiselect
    formattedLabels() {
      return this.availableLabels.map(label => ({
        id: label.id,
        name: label.title,
        color: label.color || '#1f93ff'
      }));
    },

    // Formatar atributos de contato para o multiselect
    contactCustomAttributes() {
      // Corrigido: Filtrar os atributos de contato do getter genérico
      if (this.attributesByModel) {
        return this.attributesByModel('contact_attribute');
      }
      return [];
    },
    formattedContactAttributes() {
      return this.contactCustomAttributes.map(attr => ({
        id: attr.id,
        name: attr.attribute_display_name,
      }));
    },
    // Formatar atributos de conversa para o multiselect
    conversationCustomAttributes() {
      // Corrigido: Filtrar os atributos de conversa do getter genérico
      return this.attributesByModel ? this.attributesByModel('conversation_attribute') : [];
    },
    formattedConversationAttributes() {
      return this.conversationCustomAttributes.map(attr => ({
        id: attr.id,
        name: attr.attribute_display_name,
      }));
    },

    formattedTeams() {
      return this.teams.map(team => ({
        id: team.id,
        name: team.name,
      }));
    },

    formattedAgents() {
      return this.agents.map(agent => ({
        id: agent.id,
        name: agent.name,
      }));
    },

    selectedFunnelContactAttributeIds() {
      if (!this.selectedFunnel) return [];
      if (Array.isArray(this.selectedFunnel.contact_custom_attribute_ids)) {
        return this.selectedFunnel.contact_custom_attribute_ids;
      }
      if (Array.isArray(this.selectedFunnel.contact_custom_attributes)) {
        const attrs = this.selectedFunnel.contact_custom_attributes;
        if (attrs.length > 0 && typeof attrs[0] === 'object' && attrs[0] !== null) {
          return attrs.map(attr => attr.id);
        }
        return attrs;
      }
      return [];
    },
    selectedFunnelConversationAttributeIds() {
      if (!this.selectedFunnel) return [];
      if (Array.isArray(this.selectedFunnel.conversation_custom_attribute_ids)) {
        return this.selectedFunnel.conversation_custom_attribute_ids;
      }
      if (Array.isArray(this.selectedFunnel.conversation_custom_attributes)) {
        const attrs = this.selectedFunnel.conversation_custom_attributes;
        if (attrs.length > 0 && typeof attrs[0] === 'object' && attrs[0] !== null) {
          return attrs.map(attr => attr.id);
        }
        return attrs;
      }
      return [];
    },
    
    // Computed property for editing funnel's selected contact attributes
    selectedContactAttributesForEditingFunnelModel: {
      get() {
        if (!this.editingFunnel || !this.editingFunnel.selectedContactAttributeIds) {
          return [];
        }
        const selected = this.editingFunnel.selectedContactAttributeIds.map(id =>
          this.formattedContactAttributes.find(attr => attr.id === id)
        ).filter(Boolean);
        return selected;
      },
      set(values) {
        this.editingFunnel.selectedContactAttributeIds = values.map(attr => attr.id);
      }
    },
    // Computed property for editing funnel's selected conversation attributes
    selectedConversationAttributesForEditingFunnelModel: {
      get() {
        if (!this.editingFunnel || !this.editingFunnel.selectedConversationAttributeIds) {
          return [];
        }
        const selected = this.editingFunnel.selectedConversationAttributeIds.map(id =>
          this.formattedConversationAttributes.find(attr => attr.id === id)
        ).filter(Boolean);
        return selected;
      },
      set(values) {
        this.editingFunnel.selectedConversationAttributeIds = values.map(attr => attr.id);
      }
    },

    isAdmin() {
      return this.currentUserRole === 'administrator';
    },

    userPermissions() {
      return getUserPermissions(this.currentUser, this.currentAccountId);
    },

    canCreateFunnels() {
      return this.isAdmin || hasPermissions(['kanban_manage', 'kanban_create'], this.userPermissions);
    },

    canEditFunnels() {
      return this.isAdmin || hasPermissions(['kanban_manage', 'kanban_edit'], this.userPermissions);
    },

    canDeleteFunnels() {
      return this.isAdmin || hasPermissions(['kanban_manage', 'kanban_delete'], this.userPermissions);
    },

    currentSidebarContact() {
      if (!this.activeSidebarContactId) {
        return null;
      }

      return this.getContactRecord(this.activeSidebarContactId) || null;
    },
  },
  
  watch: {
    // Atualiza os IDs de label quando o modelo de seleção muda
    selectedLabelsForNewFunnelModel(labels) {
      this.newFunnel.selectedLabelIds = labels.map(label => label.id);
    },
    // Atualiza os IDs de label quando o modelo de seleção para edição muda
    selectedLabelsForEditingFunnelModel(labels) {
      this.editingFunnel.selectedLabelIds = labels.map(label => label.id);
    },
    // Atualiza os IDs de atributos de contato
    selectedContactAttributesForNewFunnelModel(attributes) {
      this.newFunnel.selectedContactAttributeIds = attributes.map(attr => attr.id);
    },
    // Atualiza os IDs de atributos de conversa
    selectedConversationAttributesForNewFunnelModel(attributes) {
      this.newFunnel.selectedConversationAttributeIds = attributes.map(attr => attr.id);
    },
    // Atualiza os IDs de times
    selectedTeamsForNewFunnelModel(teams) {
      this.newFunnel.selectedTeamIds = teams.map(team => team.id);
    },
    // Atualiza os IDs de agentes
    selectedAgentsForNewFunnelModel(agents) {
      this.newFunnel.selectedAssigneeIds = agents.map(agent => agent.id);
    },
  },
  
  mounted() {
    this.handleResize();
    this.loadInitialData();
    this.$emitter.on(
      BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED,
      this.handleKanbanCardsRefreshRequested
    );
    document.addEventListener('click', this.handleClickOutsideSelectors);
    window.addEventListener('resize', this.handleResize);
    // Corrigido: Usar a action e o módulo corretos
    // O argumento '0' busca todos os tipos de atributos
    this.$store.dispatch('attributes/get', 0);
  },

  beforeDestroy() {
    this.$emitter.off(
      BUS_EVENTS.KANBAN_CARDS_REFRESH_REQUESTED,
      this.handleKanbanCardsRefreshRequested
    );
    document.removeEventListener('click', this.handleClickOutsideSelectors);
    window.removeEventListener('resize', this.handleResize);
    this.stopDragBoardScroll();
  },
  
  methods: {
    async createLabelForNewFunnel(title) {
      await this.createKanbanLabel(title, 'new');
    },
    async createLabelForEditingFunnel(title) {
      await this.createKanbanLabel(title, 'edit');
    },
    async createKanbanLabel(title, target) {
      const labelTitle = String(title || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
      if (!labelTitle) return;

      try {
        const label = await this.$store.dispatch('labels/create', {
          title: labelTitle,
          color: '#1f93ff',
          show_on_sidebar: true,
        });
        const createdLabel = label?.payload || label;
        const newOption = {
          id: createdLabel.id,
          name: createdLabel.title || labelTitle,
          color: createdLabel.color || '#1f93ff',
        };
        if (target === 'new') {
          this.selectedLabelsForNewFunnelModel = [
            ...this.selectedLabelsForNewFunnelModel,
            newOption,
          ];
        } else {
          this.selectedLabelsForEditingFunnelModel = [
            ...this.selectedLabelsForEditingFunnelModel,
            newOption,
          ];
        }
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('COMPANIES.BULK_ACTIONS.CREATE_LABEL_ERROR')
        );
      }
    },
    handleResize() {
      this.viewportWidth = window.innerWidth;

      if (!this.isCompactFiltersLayout) {
        this.showMobileSecondaryFilters = false;
      }
    },
    closeAllSelectors(except = null) {
      this.showFunnelSelector = except === 'funnel' ? this.showFunnelSelector : false;
      this.showFunnelActionsSelector = except === 'funnelActions' ? this.showFunnelActionsSelector : false;
      this.showCardTypeSelector = except === 'cardType' ? this.showCardTypeSelector : false;
      this.showAssigneeSelector = except === 'assignee' ? this.showAssigneeSelector : false;
      this.showTeamSelector = except === 'team' ? this.showTeamSelector : false;
      this.showSortOrderSelector =
        except === 'sortOrder' ? this.showSortOrderSelector : false;
    },

    toggleSelector(selectorName) {
      const isOpen = this[selectorName];
      this.closeAllSelectors();
      this[selectorName] = !isOpen;
    },

    toggleMobileSecondaryFilters() {
      this.showMobileSecondaryFilters = !this.showMobileSecondaryFilters;

      if (!this.showMobileSecondaryFilters) {
        this.showAssigneeSelector = false;
        this.showTeamSelector = false;
        this.showCardTypeSelector = false;
        this.showSortOrderSelector = false;
      }
    },

    handleClickOutsideSelectors() {
      this.closeAllSelectors();
    },

    startTouchCardDrag({ card, labelId, html, width, height, offsetX, offsetY, x, y }) {
      this.stopDragBoardScroll();
      this.draggingCard = JSON.parse(JSON.stringify(card));
      this.draggingFromLabelId = labelId;
      this.touchDragPreview = {
        html,
        width,
        height,
        offsetX,
        offsetY,
        x,
        y,
      };
    },

    handleTouchCardDragMove({ x, y }) {
      if (!this.draggingCard) {
        return;
      }

      this.touchDragPreview = {
        ...this.touchDragPreview,
        x,
        y,
      };
      this.updateBoardDragScroll(x);
    },

    async endTouchCardDrag({ x, y }) {
      const targetLabelId = this.getDropLabelIdFromPoint(x, y);

      if (!targetLabelId) {
        this.cancelTouchCardDrag();
        return;
      }

      await this.moveDraggedCardToLabel(targetLabelId);
    },

    cancelTouchCardDrag() {
      this.resetDraggedCardState();
    },

    getDropLabelIdFromPoint(x, y) {
      if (typeof x !== 'number' || typeof y !== 'number') {
        return null;
      }

      const target = document.elementFromPoint(x, y);
      const dropTarget = target?.closest('[data-drop-label-id]');

      return dropTarget ? dropTarget.getAttribute('data-drop-label-id') : null;
    },

    handleBoardDragOver(event) {
      if (!this.draggingCard) {
        return;
      }

      const container = this.$refs.kanbanBoardContainer;
      if (!container) {
        return;
      }

      event.preventDefault();
      this.updateBoardDragScroll(event.clientX);
    },

    updateBoardDragScroll(clientX) {
      const container = this.$refs.kanbanBoardContainer;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const threshold = Math.min(96, rect.width * 0.18);
      const canScrollLeft = container.scrollLeft > 0;
      const canScrollRight =
        container.scrollLeft + container.clientWidth < container.scrollWidth - 1;

      let direction = null;

      if (clientX <= rect.left + threshold && canScrollLeft) {
        direction = 'left';
      } else if (clientX >= rect.right - threshold && canScrollRight) {
        direction = 'right';
      }

      this.scheduleDragBoardScroll(direction);
    },

    scheduleDragBoardScroll(direction) {
      if (!direction) {
        this.stopDragBoardScroll();
        return;
      }

      if (
        this.dragScrollDirection === direction &&
        this.dragScrollTimeoutId
      ) {
        return;
      }

      this.stopDragBoardScroll();
      this.dragScrollDirection = direction;
      this.dragScrollTimeoutId = window.setTimeout(() => {
        this.performDragBoardScroll();
      }, 300);
    },

    performDragBoardScroll() {
      const container = this.$refs.kanbanBoardContainer;
      if (!container || !this.dragScrollDirection) {
        this.stopDragBoardScroll();
        return;
      }

      const atLeftEdge = container.scrollLeft <= 0;
      const atRightEdge =
        container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;

      if (
        (this.dragScrollDirection === 'left' && atLeftEdge) ||
        (this.dragScrollDirection === 'right' && atRightEdge)
      ) {
        this.stopDragBoardScroll();
        return;
      }

      const step = 336;
      const scrollDelta = this.dragScrollDirection === 'right' ? step : -step;

      container.scrollBy({
        left: scrollDelta,
        behavior: 'smooth',
      });

      const currentDirection = this.dragScrollDirection;
      this.dragScrollTimeoutId = null;
      this.scheduleDragBoardScroll(currentDirection);
    },

    stopDragBoardScroll() {
      if (this.dragScrollTimeoutId) {
        window.clearTimeout(this.dragScrollTimeoutId);
        this.dragScrollTimeoutId = null;
      }

      this.dragScrollDirection = null;
    },

    resetDraggedCardState() {
      this.stopDragBoardScroll();
      this.touchDragPreview = null;
      this.draggingCard = null;
      this.draggingFromLabelId = null;
    },

    async moveDraggedCardToLabel(targetLabelId) {
      if (!this.draggingCard || !this.draggingFromLabelId) {
        this.resetDraggedCardState();
        return;
      }

      if (String(this.draggingFromLabelId) === String(targetLabelId)) {
        this.resetDraggedCardState();
        return;
      }

      const isContactCard = this.draggingCard.type === 'contact' || 
                           (this.draggingCard.id && this.draggingCard.id.toString().startsWith('contact_')) ||
                           (this.draggingCard.contact_id && !this.draggingCard.conversation_id);
      const isCompanyCard =
        this.draggingCard.type === 'company' ||
        (this.draggingCard.id && this.draggingCard.id.toString().startsWith('company_'));
                           
      const availableLabels = this.availableLabels;
      const sourceLabel = availableLabels.find(
        label => String(label.id) === String(this.draggingFromLabelId)
      );
      const targetLabel = availableLabels.find(
        label => String(label.id) === String(targetLabelId)
      );

      if (!sourceLabel || !targetLabel) {
        this.resetDraggedCardState();
        return;
      }

      try {
        await this.$store.dispatch('kanban/moveCard', {
          cardId: this.draggingCard.id,
          conversationId: isContactCard || isCompanyCard ? null : this.draggingCard.id,
          contactId: isContactCard
            ? (this.draggingCard.contact_id || this.draggingCard.id.replace('contact_', ''))
            : null,
          companyId: isCompanyCard
            ? (this.draggingCard.company_id || this.draggingCard.id.replace('company_', ''))
            : null,
          sourceLabelId: sourceLabel.id,
          targetLabelId: targetLabel.id,
          sourceLabelTitle: sourceLabel.title,
          targetLabelTitle: targetLabel.title,
          type: isCompanyCard ? 'company' : isContactCard ? 'contact' : 'conversation'
        });
      } finally {
        this.resetDraggedCardState();
      }
    },

    handleKanbanCardsRefreshRequested(payload = {}) {
      if (!this.selectedFunnelId) {
        return;
      }

      const shouldRefreshColumns =
        payload.scope === 'labels' ||
        Boolean(this.selectedAssigneeId) ||
        Boolean(this.selectedTeamId);

      if (shouldRefreshColumns) {
        this.refreshAffectedKanbanColumns(payload);
        return;
      }

      if (payload.cardId && payload.entityType) {
        this.$store.dispatch('kanban/refreshCardData', {
          cardId: payload.cardId,
          type: payload.entityType,
          updatedData: payload.updatedData || null,
        });
        return;
      }

      this.reloadKanbanCards();
    },

    getVisibleLabelIdsForCard(cardId) {
      return Object.keys(this.cardsByLabel || {}).filter(labelId =>
        (this.cardsByLabel[labelId] || []).some(
          card => String(card.id) === String(cardId)
        )
      );
    },

    getFunnelLabelIdsByTitles(labelTitles = []) {
      if (!Array.isArray(labelTitles) || !labelTitles.length) {
        return [];
      }

      const normalizedTitles = labelTitles.map(title => String(title));
      return this.selectedFunnelLabels
        .filter(label => normalizedTitles.includes(label.title))
        .map(label => String(label.id));
    },

    refreshAffectedKanbanColumns(payload = {}) {
      const currentVisibleLabelIds = payload.cardId
        ? this.getVisibleLabelIdsForCard(payload.cardId)
        : [];
      const previousLabelIds = this.getFunnelLabelIdsByTitles(
        payload.previousLabels || []
      );
      const nextLabelIds = this.getFunnelLabelIdsByTitles(
        payload.nextLabels || payload.labelTitles || []
      );

      const affectedLabelIds = [
        ...new Set([
          ...currentVisibleLabelIds,
          ...previousLabelIds,
          ...nextLabelIds,
        ]),
      ];

      if (!affectedLabelIds.length) {
        this.reloadKanbanCards();
        return;
      }

      affectedLabelIds.forEach(labelId => {
        this.$store.dispatch('kanban/fetchCards', {
          funnelId: this.selectedFunnelId,
          labelId,
          page: 1,
          search: this.columnSearchTerms[labelId] || '',
          ...this.getActiveKanbanFilters(),
        });
      });
    },

    // Alterna a visibilidade do seletor de tipo de cards
    toggleCardTypeSelector() {
      this.toggleSelector('showCardTypeSelector');
    },

    toggleAssigneeSelector() {
      this.toggleSelector('showAssigneeSelector');
    },

    toggleTeamSelector() {
      this.toggleSelector('showTeamSelector');
    },

    toggleFunnelActionsSelector() {
      this.toggleSelector('showFunnelActionsSelector');
    },

    getActiveKanbanFilters() {
      return {
        cardTypes: this.selectedCardTypes,
        assigneeId: this.selectedAssigneeId,
        teamId: this.selectedTeamId,
        sortOrder: this.selectedSortOrder,
      };
    },

    toggleSortOrderSelector() {
      this.toggleSelector('showSortOrderSelector');
    },

    // Troca a ordenação e reinicia a listagem (página 1) de todas as colunas
    // visíveis — sem isso, misturar itens ordenados de formas diferentes
    // dentro da mesma coluna deixaria cards repetidos ou faltando.
    selectSortOrder(sortOrder) {
      if (!SORT_ORDERS.includes(sortOrder)) return;

      this.selectedSortOrder = sortOrder;
      localStorage.setItem('kanban_sort_order', sortOrder);
      this.showSortOrderSelector = false;
      this.reloadKanbanCards();
    },

    reloadKanbanCards() {
      if (!this.selectedFunnelId) return;

      return this.$store.dispatch('kanban/fetchCards', {
        funnelId: this.selectedFunnelId,
        ...this.getActiveKanbanFilters(),
      });
    },
    
    isCardTypeSelected(type) {
      if (type === 'all') {
        return this.selectedCardTypes.length === DEFAULT_CARD_TYPES.length;
      }

      return this.selectedCardTypes.includes(type);
    },

    persistSelectedCardTypes() {
      localStorage.setItem(
        'kanban_card_types',
        JSON.stringify(this.selectedCardTypes)
      );
      localStorage.removeItem('kanban_card_type');
    },

    toggleCardType(type) {
      if (type === 'all') {
        this.selectedCardTypes = this.isCardTypeSelected('all')
          ? []
          : [...DEFAULT_CARD_TYPES];
      } else if (this.selectedCardTypes.includes(type)) {
        this.selectedCardTypes = this.selectedCardTypes.filter(
          selectedType => selectedType !== type
        );
      } else {
        this.selectedCardTypes = [...this.selectedCardTypes, type];
      }

      if (!this.selectedCardTypes.length) {
        this.selectedCardTypes = [...DEFAULT_CARD_TYPES];
      }

      this.persistSelectedCardTypes();
      this.reloadKanbanCards();
      if (this.currentDetailLabelId) {
        this.fetchColumnDetails(this.currentDetailLabelId);
      }
    },

    selectAssigneeFilter(assigneeId) {
      this.selectedAssigneeId = assigneeId;
      localStorage.setItem('kanban_assignee_id', assigneeId);
      this.showAssigneeSelector = false;
      this.reloadKanbanCards();
      if (this.currentDetailLabelId) {
        this.fetchColumnDetails(this.currentDetailLabelId);
      }
    },

    selectTeamFilter(teamId) {
      this.selectedTeamId = teamId;
      localStorage.setItem('kanban_team_id', teamId);
      this.showTeamSelector = false;
      this.reloadKanbanCards();
      if (this.currentDetailLabelId) {
        this.fetchColumnDetails(this.currentDetailLabelId);
      }
    },
    
    // Executa a pesquisa no servidor
    searchColumn(labelId, searchTerm) {
      if (!this.selectedFunnelId) return;
      
      this.$store.dispatch('kanban/fetchCards', {
        funnelId: this.selectedFunnelId,
        labelId: labelId,
        page: 1, // Resetar para página 1 na pesquisa
        search: searchTerm,
        ...this.getActiveKanbanFilters(),
      });
    },
    
    // Manipula scroll infinito da coluna
    handleColumnScroll(event, labelId) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const meta = this.getMetaForLabel(labelId);
      
      // Se estiver perto do fim (200px), tem mais páginas e não está carregando
      if (scrollTop + clientHeight >= scrollHeight - 200 && 
          meta.has_more && 
          !this.loadingMore[labelId]) {
            
        this.loadMoreCards(labelId);
      }
    },
    
    // Carrega mais cards (paginação)
    async loadMoreCards(labelId) {
      if (!this.selectedFunnelId) return;
      
      const meta = this.getMetaForLabel(labelId);
      const nextPage = (meta.page || 1) + 1;
      const searchTerm = this.columnSearchTerms[labelId] || '';
      
      this.$set(this.loadingMore, labelId, true);
      
      try {
        await this.$store.dispatch('kanban/fetchMoreCards', {
          funnelId: this.selectedFunnelId,
          labelId: labelId,
          page: nextPage,
          search: searchTerm,
          ...this.getActiveKanbanFilters(),
        });
      } catch (e) {
        console.error('Error loading more cards:', e);
      } finally {
        this.$set(this.loadingMore, labelId, false);
      }
    },
    
    // Filtra cards APENAS pelo tipo selecionado (a pesquisa agora é server-side)
    getFilteredCardsForLabel(labelId) {
      return this.getCardsForLabel(labelId);
    },

    async openColumnDetails(label) {
      this.currentDetailLabelId = label.id;
      this.showColumnDetailsDrawer = true;
      await this.fetchColumnDetails(label.id);
    },
    
    closeColumnDetails() {
      this.showColumnDetailsDrawer = false;
      this.selectedColumnDetails = null;
      this.currentDetailLabelId = null;
    },

    async fetchColumnDetails(labelId) {
      if (!this.selectedFunnelId || !labelId) return;
      
      this.isFetchingColumnDetails = true;
      try {
        const response = await this.$store.dispatch('kanban/fetchColumnDetails', {
          funnelId: this.selectedFunnelId,
          labelId: labelId,
          search: this.drawerSearchTerm,
          ...this.getActiveKanbanFilters(),
        });
        this.selectedColumnDetails = response;
      } catch (error) {
        useAlert('Erro ao carregar detalhes da coluna');
      } finally {
        this.isFetchingColumnDetails = false;
      }
    },
    
    // Carrega os dados iniciais
    async loadInitialData() {
      await this.$store.dispatch('kanban/fetchFunnels');
      await this.$store.dispatch('kanban/fetchAvailableLabels');
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      
      const cachedFunnelId = localStorage.getItem('kanban_selected_funnel_id');
      if (cachedFunnelId) {
         const funnelExists = this.funnels.find(f => f.id == cachedFunnelId);
         if (funnelExists) {
             this.$store.commit('kanban/SET_SELECTED_FUNNEL_ID', funnelExists.id);
         }
      }

      // Se tiver um funil selecionado, carrega os cards
      if (this.selectedFunnelId) {
        this.reloadKanbanCards();
      }
    },
    
    // Toggle do seletor de funil
    toggleFunnelSelector() {
      this.toggleSelector('showFunnelSelector');
    },

    openEditFunnelFromMenu() {
      this.showFunnelActionsSelector = false;
      this.openEditFunnelModal();
    },

    openCreateFunnelFromMenu() {
      this.showFunnelActionsSelector = false;
      this.showCreateFunnelModal = true;
    },

    openDeleteFunnelFromMenu() {
      this.showFunnelActionsSelector = false;
      this.showDeleteFunnelModal = true;
    },

    truncateText(value, maxLength = 20) {
      if (!value) {
        return '';
      }

      return value.length > maxLength
        ? `${value.slice(0, maxLength)}...`
        : value;
    },
    
    // Seleciona um funil e carrega seus cards
    selectFunnel(funnel) {
      this.$store.commit('kanban/SET_SELECTED_FUNNEL_ID', funnel.id);
      localStorage.setItem('kanban_selected_funnel_id', funnel.id);
      this.$store.dispatch('kanban/fetchCards', {
        funnelId: funnel.id,
        ...this.getActiveKanbanFilters(),
      });
      this.showFunnelSelector = false;
    },
    
    // Obtém uma label pelo ID
    getLabelById(labelId) {
      return this.availableLabels.find(label => label.id === labelId) || {};
    },
    
    // Movimenta uma tag para cima na lista de criação
    moveTagUp(index) {
      if (index > 0) {
        const tags = [...this.newFunnel.selectedLabelIds];
        const temp = tags[index];
        tags[index] = tags[index - 1];
        tags[index - 1] = temp;
        this.newFunnel.selectedLabelIds = tags;
      }
    },
    
    // Movimenta uma tag para baixo na lista de criação
    moveTagDown(index) {
      if (index < this.newFunnel.selectedLabelIds.length - 1) {
        const tags = [...this.newFunnel.selectedLabelIds];
        const temp = tags[index];
        tags[index] = tags[index + 1];
        tags[index + 1] = temp;
        this.newFunnel.selectedLabelIds = tags;
      }
    },
    
    // Movimenta uma tag para cima na lista de edição
    moveEditTagUp(index) {
      if (index > 0) {
        const tags = [...this.editingFunnel.selectedLabelIds];
        const temp = tags[index];
        tags[index] = tags[index - 1];
        tags[index - 1] = temp;
        this.editingFunnel.selectedLabelIds = tags;
      }
    },
    
    // Movimenta uma tag para baixo na lista de edição
    moveEditTagDown(index) {
      if (index < this.editingFunnel.selectedLabelIds.length - 1) {
        const tags = [...this.editingFunnel.selectedLabelIds];
        const temp = tags[index];
        tags[index] = tags[index + 1];
        tags[index + 1] = temp;
        this.editingFunnel.selectedLabelIds = tags;
      }
    },
    
    // Abre o modal de edição de funil
    openEditFunnelModal() {
      if (!this.selectedFunnel) return;

      this.editingFunnel = {
        id: this.selectedFunnel.id,
        name: this.selectedFunnel.name,
        selectedLabelIds: this.selectedFunnel.label_ids || [],
        selectedContactAttributeIds: this.selectedFunnelContactAttributeIds,
        selectedConversationAttributeIds: this.selectedFunnelConversationAttributeIds,
        selectedTeamIds: this.selectedFunnel.team_ids || [],
        selectedAssigneeIds: this.selectedFunnel.assignee_ids || [],
      };
      
      // Atualiza o modelo de seleção para corresponder aos IDs, mantendo a ordem original
      this.selectedLabelsForEditingFunnelModel = this.editingFunnel.selectedLabelIds.map(labelId =>
        this.formattedLabels.find(label => label.id === labelId)
      ).filter(Boolean);

      this.selectedTeamsForEditingFunnelModel = this.editingFunnel.selectedTeamIds.map(teamId =>
        this.formattedTeams.find(team => team.id === teamId)
      ).filter(Boolean);

      this.selectedAgentsForEditingFunnelModel = this.editingFunnel.selectedAssigneeIds.map(agentId =>
        this.formattedAgents.find(agent => agent.id === agentId)
      ).filter(Boolean);
      
      this.showEditFunnelModal = true;
    },
    
    // Cria um novo funil
    async createFunnel() {
      if (!this.newFunnel.name || this.newFunnel.selectedLabelIds.length === 0) {
        return; // Validação básica
      }
      
      await this.$store.dispatch('kanban/createFunnel', { // Corrigido para encapsular os dados
        kanban_funnel: {
          name: this.newFunnel.name,
          label_ids: this.newFunnel.selectedLabelIds,
          contact_custom_attribute_ids: this.newFunnel.selectedContactAttributeIds,
          conversation_custom_attribute_ids: this.newFunnel.selectedConversationAttributeIds,
          team_ids: this.newFunnel.selectedTeamIds,
          assignee_ids: this.newFunnel.selectedAssigneeIds,
        }
      });

      // Carrega cards do novo funil se foi criado com sucesso
      if (!this.getError('create')) {
        this.$store.dispatch('kanban/fetchCards', {
          funnelId: this.selectedFunnelId,
          ...this.getActiveKanbanFilters(),
        });
        
        useAlert(this.$t('KANBAN.FUNNEL.CREATE_SUCCESS', { name: this.newFunnel.name }));
      } else {
        useAlert(this.$t('KANBAN.FUNNEL.CREATE_ERROR', { name: this.newFunnel.name }));
      }
      this.showCreateFunnelModal = false;
      // Resetar o formulário
      this.newFunnel = {
        name: '',
        selectedLabelIds: [],
        selectedContactAttributeIds: [],
        selectedConversationAttributeIds: [],
        selectedTeamIds: [],
        selectedAssigneeIds: [],
      };
      this.selectedLabelsForNewFunnelModel = [];
      this.selectedContactAttributesForNewFunnelModel = [];
      this.selectedConversationAttributesForNewFunnelModel = [];
      this.selectedTeamsForNewFunnelModel = [];
      this.selectedAgentsForNewFunnelModel = [];
    },
    
    // Atualiza um funil existente
    async updateFunnel() {
      if (!this.editingFunnel.id || !this.editingFunnel.name || this.editingFunnel.selectedLabelIds.length === 0) {
        return; // Validação básica
      }
      
      this.editingFunnel.selectedTeamIds = this.selectedTeamsForEditingFunnelModel.map(t => t.id);
      this.editingFunnel.selectedAssigneeIds = this.selectedAgentsForEditingFunnelModel.map(a => a.id);

      await this.$store.dispatch('kanban/updateFunnel', {
        id: this.editingFunnel.id, // O ID fica fora do objeto do funil
        kanban_funnel: { // Corrigido para encapsular os dados
          name: this.editingFunnel.name,
          label_ids: this.editingFunnel.selectedLabelIds,
          contact_custom_attribute_ids: this.editingFunnel.selectedContactAttributeIds,
          conversation_custom_attribute_ids: this.editingFunnel.selectedConversationAttributeIds,
          team_ids: this.editingFunnel.selectedTeamIds,
          assignee_ids: this.editingFunnel.selectedAssigneeIds,
        }
      });
      
      // Recarrega dados do funil se atualizado com sucesso
      if (!this.getError('update')) {
        this.$store.dispatch('kanban/fetchCards', {
          funnelId: this.selectedFunnelId,
          ...this.getActiveKanbanFilters(),
        });
        this.showEditFunnelModal = false;
        useAlert(this.$t('KANBAN.FUNNEL.UPDATE_SUCCESS', { name: this.editingFunnel.name }));
      } else {
        useAlert(this.$t('KANBAN.FUNNEL.UPDATE_ERROR', { name: this.editingFunnel.name }));
      }
    },
    
    // Exclui um funil
    async deleteFunnel() {
      if (!this.selectedFunnel) return;
      
      // Armazena o nome do funil antes de excluí-lo
      const deletedFunnelName = this.selectedFunnel.name;
      
      await this.$store.dispatch('kanban/deleteFunnel', this.selectedFunnel.id);
      
      if (!this.getError('delete')) {
        this.showDeleteFunnelModal = false;
        useAlert(this.$t('KANBAN.FUNNEL.DELETE_SUCCESS', { name: deletedFunnelName }));
      } else {
        useAlert(this.$t('KANBAN.FUNNEL.DELETE_ERROR', { name: deletedFunnelName }));
      }
    },
    
    // Inicia o arrastar de um card
    dragStart(event, card, sourceLabelId) {
      this.stopDragBoardScroll();
      // IMPORTANTE: Armazenar uma cópia do card, não apenas a referência
      this.draggingCard = JSON.parse(JSON.stringify(card));
      this.draggingFromLabelId = sourceLabelId;
    },
    
    // Finaliza o arrastar quando o card é solto ou a operação é cancelada
    handleDragEnd(event) {
      this.stopDragBoardScroll();
      // Podemos limpar os dados se o drop não ocorreu em uma coluna válida
      if (event.dataTransfer.dropEffect === 'none') {
        this.resetDraggedCardState();
      }
    },
    
    // Recebe um card arrastado
    async dropCard(event, targetLabelId) {
      this.stopDragBoardScroll();
      
      // Verifica se temos as informações necessárias
      if (!this.draggingCard || !this.draggingFromLabelId) {
        
        // Tentar recuperar os dados do dataTransfer como fallback
        try {
          const jsonData = event.dataTransfer.getData('application/json');
          if (jsonData) {
            const data = JSON.parse(jsonData);
            
            // Se não temos o draggingCard, tente encontrá-lo usando o ID
            if (!this.draggingCard && data.cardId && data.sourceLabelId) {
              const sourceList = this.$store.getters['kanban/getCardsForLabel'](data.sourceLabelId);
              this.draggingCard = sourceList.find(c => c.id === data.cardId);
              this.draggingFromLabelId = data.sourceLabelId;
            }
          }
        } catch (e) {
          throw e;
        }
        
        // Se ainda não temos os dados necessários, abortamos
        if (!this.draggingCard || !this.draggingFromLabelId) {
          return;
        }
      }
      
      await this.moveDraggedCardToLabel(targetLabelId);
    },
    
    closeCardSidebar() {
      const shouldClearSelectedState = this.activeSidebarType === 'conversation';

      this.activeSidebarType = null;
      this.activeSidebarConversationId = null;
      this.activeSidebarInboxId = null;
      this.activeSidebarContactId = null;
      this.isCardSidebarLoading = false;

      if (shouldClearSelectedState) {
        this.$store.dispatch('clearSelectedState');
      }
    },

    async openCardSidebar(card) {
      if (!card) return;

      if (card.type === 'contact') {
        await this.openContactSidebar(card);
        return;
      }

      if (card.type === 'company') {
        this.$router.push({
          name: 'company_profile_dashboard',
          params: {
            accountId: this.$route.params.accountId,
            companyId: card.company_id,
          },
        });
        return;
      }

      await this.openConversationSidebar(card);
    },

    async openContactSidebar(card) {
      const contactId = card.contact_id;

      if (!contactId) return;

      this.closeCardSidebar();
      this.activeSidebarType = 'contact';
      this.activeSidebarContactId = contactId;
      this.isCardSidebarLoading = true;

      try {
        await this.$store.dispatch('contacts/show', { id: contactId });
        if (!this.getContactRecord(contactId)) {
          this.closeCardSidebar();
        }
      } finally {
        this.isCardSidebarLoading = false;
      }
    },

    async openConversationSidebar(card) {
      const conversationId = card.id;

      if (!conversationId) return;

      this.closeCardSidebar();
      this.activeSidebarType = 'conversation';
      this.activeSidebarConversationId = conversationId;
      this.activeSidebarInboxId = card.inbox_id;
      this.isCardSidebarLoading = true;

      try {
        await this.$store.dispatch('getConversation', conversationId);

        const conversation = this.getConversationById(conversationId);
        if (!conversation) {
          this.closeCardSidebar();
          return;
        }

        await this.$store.dispatch('setActiveChat', {
          data: conversation,
        });
        await this.$store.dispatch('conversationLabels/get', conversationId);
      } finally {
        this.isCardSidebarLoading = false;
      }
    },
    
    // Métodos para drag-and-drop de etiquetas
    startDragLabel(event, index, mode) {
      event.dataTransfer.setData('text/plain', index);
      event.dataTransfer.setData('mode', mode);
      event.dataTransfer.effectAllowed = 'move';
      
      // Adiciona uma classe visual para indicar o dragging
      event.target.classList.add('dragging');
    },
    
    dropLabel(event, targetIndex, mode) {
      event.preventDefault();
      const sourceIndex = parseInt(event.dataTransfer.getData('text/plain'));
      const dragMode = event.dataTransfer.getData('mode');
      
      // Verifica se o modo corresponde (para evitar mix entre create/edit)
      if (dragMode !== mode || sourceIndex === targetIndex) {
        return;
      }
      
      // Determina qual array usar baseado no modo
      const labelsArray = mode === 'create' ? this.newFunnel.selectedLabelIds : this.editingFunnel.selectedLabelIds;
      
      // Cria uma cópia do array para manipulação
      const newLabelsArray = [...labelsArray];
      
      // Remove o item da posição original
      const [movedLabel] = newLabelsArray.splice(sourceIndex, 1);
      
      // Insere o item na nova posição
      newLabelsArray.splice(targetIndex, 0, movedLabel);
      
      // Atualiza o array correto
      if (mode === 'create') {
        this.newFunnel.selectedLabelIds = newLabelsArray;
      } else {
        this.editingFunnel.selectedLabelIds = newLabelsArray;
      }
      
      // Remove a classe de dragging de todos os elementos
      document.querySelectorAll('.dragging').forEach(el => {
        el.classList.remove('dragging');
      });
    },
  }
};
</script>

<style scoped>
.kanban-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.kanban-board-container {
  flex-grow: 1;
  overflow-x: auto;
  width: 100%;
}

.kanban-board {
  display: inline-flex;
  min-height: 100%;
  min-width: max-content;
  padding-bottom: 1rem;
}

.kanban-filter-trigger {
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.kanban-dropdown-pane {
  background-color: rgb(var(--fd-surface));
  border: 1px solid rgb(var(--fd-line-faint));
  color: rgb(var(--fd-muted));
}

.kanban-dropdown-item {
  color: rgb(var(--fd-muted));
}

.kanban-dropdown-item:hover {
  background-color: rgb(var(--fd-surface-sunken));
  color: rgb(var(--fd-body));
}

.kanban-dropdown-item--danger {
  color: rgb(var(--color-ruby-800));
}

.kanban-dropdown-item--danger:hover {
  color: rgb(var(--color-ruby-800));
}

.dark .kanban-dropdown-pane {
  background-color: rgb(var(--fd-surface-raised) / 0.98);
  border-color: rgb(var(--fd-line-strong) / 0.9);
  color: rgb(var(--fd-body));
}

.dark .kanban-dropdown-item {
  color: rgb(var(--fd-body));
}

.dark .kanban-dropdown-item:hover {
  background-color: rgb(var(--fd-surface-raised) / 0.9);
  color: rgb(var(--fd-body));
}

.dark .kanban-dropdown-item--danger {
  color: rgb(var(--fd-subtle));
}

.dark .kanban-dropdown-item--danger:hover {
  color: rgb(var(--fd-body));
}

.column-content {
  max-height: calc(100vh - 165px);
}

/* Estilo para cards sendo arrastados */
.kanban-card:active {
  cursor: grabbing;
  opacity: 0.6;
}

/* Estilos para drag-and-drop de etiquetas */
.cursor-move {
  cursor: grab;
}

.cursor-move.dragging {
  opacity: 0.5;
  transform: scale(0.98);
}

/* Indicador visual para área de drop */
.cursor-move:not(.dragging):hover {
  background-color: rgb(var(--color-flipdesk-500) / 0.30);
}

/* Numeração da ordem das etiquetas */
.order-number {
  min-width: 24px;
  height: 24px;
  font-size: 11px;
  font-weight: 600;
}

.touch-drag-preview {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 70;
  pointer-events: none;
}

.touch-drag-preview-card {
  opacity: 0.96;
}

.mobile-filters-slide-enter-active,
.mobile-filters-slide-leave-active {
  overflow: hidden;
  transition: opacity 0.22s ease, transform 0.22s ease, max-height 0.22s ease;
}

.mobile-filters-slide-enter,
.mobile-filters-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  max-height: 0;
}

.mobile-filters-slide-enter-to,
.mobile-filters-slide-leave {
  opacity: 1;
  transform: translateY(0);
  max-height: 10rem;
}

@media (max-width: 425px) {
  .kanban-board-container {
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
  }

  .kanban-board {
    padding-inline: max(0px, calc(50% - 160px));
  }

  .kanban-column {
    scroll-snap-align: center;
    scroll-snap-stop: always;
  }
}
</style>
